/**
 * The application route table.
 *
 * Kept out of `main.tsx` so tests can mount the real routes with
 * `createMemoryRouter` — the guided lessons deep-link into specific screens,
 * and a route that silently stops matching would break a lesson step with no
 * other signal.
 */
import { lazy } from 'react'
import type { RouteObject } from 'react-router'
import { App } from '@/App'
import { TourLayout } from '@/player/TourLayout'
import StocktakeSetupScreen from '@/screens/stocktake/StocktakeSetupScreen'
import StocktakeStartScreen from '@/screens/stocktake/StocktakeStartScreen'

const ArticlepediaScreen = lazy(() => import('@/screens/articlepedia/ArticlepediaScreen'))
const ArticlepediaDetailScreen = lazy(
  () => import('@/screens/articlepedia/ArticlepediaDetailScreen'),
)
const ArticlepediaSearchScreen = lazy(
  () => import('@/screens/articlepedia/ArticlepediaSearchScreen'),
)
const ArticlepediaVendorScreen = lazy(
  () => import('@/screens/articlepedia/ArticlepediaVendorScreen'),
)
const ExpiryListScreen = lazy(() => import('@/screens/expiry-check/ExpiryListScreen'))
const ExpiryScanScreen = lazy(() => import('@/screens/expiry-check/ExpiryScanScreen'))
const ExpirySearchScreen = lazy(() => import('@/screens/expiry-check/ExpirySearchScreen'))
const ExpiryDetailScreen = lazy(() => import('@/screens/expiry-check/ExpiryDetailScreen'))
const ExpiryWriteOffScreen = lazy(() => import('@/screens/expiry-check/ExpiryWriteOffScreen'))
const LessonDetailScreen = lazy(() =>
  import('@/dashboard/LessonDetailScreen').then((m) => ({ default: m.LessonDetailScreen })),
)
const ModuleLessonsScreen = lazy(() =>
  import('@/dashboard/ModuleLessonsScreen').then((m) => ({ default: m.ModuleLessonsScreen })),
)
const RequiredTrainingScreen = lazy(() =>
  import('@/dashboard/RequiredTrainingScreen').then((m) => ({ default: m.RequiredTrainingScreen })),
)
const GalleryPage = lazy(() => import('@/gallery/GalleryPage'))
const LessonStart = lazy(() =>
  import('@/player/LessonStart').then((m) => ({ default: m.LessonStart })),
)
const GuidedCompleteScreen = lazy(() =>
  import('@/player/GuidedCompleteScreen').then((m) => ({ default: m.GuidedCompleteScreen })),
)
const LessonCompleteScreen = lazy(() =>
  import('@/player/LessonCompleteScreen').then((m) => ({ default: m.LessonCompleteScreen })),
)
const HomeScreen = lazy(() => import('@/screens/home/HomeScreen'))
const CartScreen = lazy(() => import('@/screens/cart/CartScreen'))
const ScanScreen = lazy(() => import('@/screens/ordering/screens/ScanScreen'))
const OrdersScreen = lazy(() => import('@/screens/ordering/screens/OrdersScreen'))
const OrderDetailScreen = lazy(() => import('@/screens/ordering/screens/OrderDetailScreen'))
const QpoScanScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOScanScreen'))
const QpoSupplierScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOSupplierScreen'))
const FeaturesScreen = lazy(() => import('@/screens/features/FeaturesScreen'))
const QPOSupplierSearchScreen = lazy(
  () => import('@/screens/ordering/screens/qpo/QPOSupplierSearchScreen'),
)
const QPOSkuScanScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOSkuScanScreen'))
const QPOSkuSearchScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOSkuSearchScreen'))
const QPOVerifyScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOVerifyScreen'))
const QPOSummaryScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOSummaryScreen'))
const QPOSuccessScreen = lazy(() => import('@/screens/ordering/screens/qpo/QPOSuccessScreen'))
const QPOShortfallClaimsScreen = lazy(
  () => import('@/screens/ordering/screens/qpo/QPOShortfallClaimsScreen'),
)
const QPOShortfallClaimsSuccessScreen = lazy(
  () => import('@/screens/ordering/screens/qpo/QPOShortfallClaimsSuccessScreen'),
)
const ReceiveDetailScreen = lazy(() => import('@/screens/receiving/screens/ReceiveDetailScreen'))
const GrQtyInputScreen = lazy(() => import('@/screens/receiving/screens/GrQtyInputScreen'))
const GrScanScreen = lazy(() => import('@/screens/receiving/screens/GrScanScreen'))
const GrPrintSlipScreen = lazy(() => import('@/screens/receiving/screens/GrPrintSlipScreen'))
const GrSummaryScreen = lazy(() => import('@/screens/receiving/screens/GrSummaryScreen'))
const GrSuccessScreen = lazy(() => import('@/screens/receiving/screens/GrSuccessScreen'))
const HistoryScreen = lazy(() => import('@/screens/history/HistoryScreen'))
const HistoryDetailScreen = lazy(() => import('@/screens/history/HistoryDetailScreen'))
const AdjustmentDetailScreen = lazy(() => import('@/screens/history/AdjustmentDetailScreen'))
const GiDetailScreen = lazy(() => import('@/screens/history/GiDetailScreen'))
const DOReceiveDetailScreen = lazy(
  () => import('@/screens/receiving/screens/DOReceiveDetailScreen'),
)
const DOAddShipmentScreen = lazy(() => import('@/screens/receiving/screens/DOAddShipmentScreen'))
const DOVerifyPalletsScreen = lazy(
  () => import('@/screens/receiving/screens/DOVerifyPalletsScreen'),
)
const DOSummaryScreen = lazy(() => import('@/screens/receiving/screens/DOSummaryScreen'))
const DOScanTruckScreen = lazy(() => import('@/screens/receiving/screens/DOScanTruckScreen'))
const ReceivingShortfallClaimsScreen = lazy(
  () => import('@/screens/receiving/screens/ReceivingShortfallClaimsScreen'),
)
const ReceivingShortfallClaimsSuccessScreen = lazy(
  () => import('@/screens/receiving/screens/ShortfallClaimsSuccessScreen'),
)
const ReturnSelectVendorScreen = lazy(
  () => import('@/screens/returns/screens/ReturnSelectVendorScreen'),
)
const ReturnAddItemsScreen = lazy(() => import('@/screens/returns/screens/ReturnAddItemsScreen'))
const ReturnScanScreen = lazy(() => import('@/screens/returns/screens/ReturnScanScreen'))
const ReturnSearchProductScreen = lazy(
  () => import('@/screens/returns/screens/ReturnSearchProductScreen'),
)
const ReturnPendingScreen = lazy(() => import('@/screens/returns/screens/ReturnPendingScreen'))
const RstoDetailScreen = lazy(() => import('@/screens/returns/screens/RstoDetailScreen'))
const IssueGoodsScreen = lazy(() => import('@/screens/returns/screens/IssueGoodsScreen'))
const IssueGoodsSuccessScreen = lazy(
  () => import('@/screens/returns/screens/IssueGoodsSuccessScreen'),
)
const IssueGoodsSummaryScreen = lazy(
  () => import('@/screens/returns/screens/IssueGoodsSummaryScreen'),
)
const GiForDisposalScreen = lazy(() => import('@/screens/returns/screens/GiForDisposalScreen'))
const GiForDisposalSummaryScreen = lazy(
  () => import('@/screens/returns/screens/GiForDisposalSummaryScreen'),
)
const SohAdjustmentScreen = lazy(() => import('@/screens/soh-adjustment/SohAdjustmentScreen'))
const SohAdjustmentDetailScreen = lazy(
  () => import('@/screens/soh-adjustment/SohAdjustmentDetailScreen'),
)
const SohAdjustmentScanScreen = lazy(
  () => import('@/screens/soh-adjustment/SohAdjustmentScanScreen'),
)
const SohAdjustmentCreateScreen = lazy(
  () => import('@/screens/soh-adjustment/SohAdjustmentCreateScreen'),
)
const SohAdjustmentSubmittedScreen = lazy(
  () => import('@/screens/soh-adjustment/SohAdjustmentSubmittedScreen'),
)
const StocktakeSetupEditScreen = lazy(() => import('@/screens/stocktake/StocktakeSetupEditScreen'))
const StocktakeScanLocScreen = lazy(() => import('@/screens/stocktake/StocktakeScanLocScreen'))
const StocktakeScanSKUScreen = lazy(() => import('@/screens/stocktake/StocktakeScanSKUScreen'))
const StocktakeSearchScreen = lazy(() => import('@/screens/stocktake/StocktakeSearchScreen'))
const StocktakeSkuSearchScreen = lazy(() => import('@/screens/stocktake/StocktakeSkuSearchScreen'))
const StocktakeScanListScreen = lazy(() => import('@/screens/stocktake/StocktakeScanListScreen'))
const StocktakeSuccessScreen = lazy(() => import('@/screens/stocktake/StocktakeSuccessScreen'))
const CreateTaskScreen = lazy(() => import('@/screens/task-management/CreateTaskScreen'))
const TaskDetailScreen = lazy(() => import('@/screens/task-management/TaskDetailScreen'))
const EditTaskScreen = lazy(() => import('@/screens/task-management/EditTaskScreen'))
const TaskStartScreen = lazy(() => import('@/screens/task-management/TaskStartScreen'))
const AllTasksScreen = lazy(() => import('@/screens/task-management/AllTasksScreen'))
const StoreTasksSearchScreen = lazy(
  () => import('@/screens/task-management/StoreTasksSearchScreen'),
)
const AccessibilityScreen = lazy(() =>
  import('@/accessibility/AccessibilityScreen').then((m) => ({
    default: m.AccessibilityScreen,
  })),
)

// TourLayout is a pathless layout route: it renders the matched screen plus the
// coach-mark overlay, so a lesson runs on the real screens instead of replicas.
export const appRoutes: RouteObject[] = [
  {
    element: <TourLayout />,
    children: [
      { path: '/', element: <App /> },
      { path: '/articlepedia', element: <ArticlepediaScreen /> },
      { path: '/articlepedia/detail', element: <ArticlepediaDetailScreen /> },
      { path: '/articlepedia/search', element: <ArticlepediaSearchScreen /> },
      { path: '/articlepedia/vendor', element: <ArticlepediaVendorScreen /> },
      { path: '/expiry', element: <ExpiryListScreen /> },
      { path: '/expiry/scan', element: <ExpiryScanScreen /> },
      { path: '/expiry/search', element: <ExpirySearchScreen /> },
      { path: '/expiry/write-off', element: <ExpiryWriteOffScreen /> },
      { path: '/expiry/detail/:id', element: <ExpiryDetailScreen /> },
      { path: '/gallery', element: <GalleryPage /> },
      { path: '/lesson-detail/:id', element: <LessonDetailScreen /> },
      { path: '/module/:id', element: <ModuleLessonsScreen /> },
      { path: '/required-training', element: <RequiredTrainingScreen /> },
      { path: '/lesson/:subModuleId', element: <LessonStart /> },
      // The two ends of a lesson. Both sit under TourLayout like everything
      // else, so the overlays stay mounted and stay inert — neither screen
      // carries `?tour=` or `?sim=`, which is precisely what ends the run.
      { path: '/lesson/:subModuleId/guided-complete', element: <GuidedCompleteScreen /> },
      { path: '/lesson/:subModuleId/complete', element: <LessonCompleteScreen /> },
      { path: '/home', element: <HomeScreen /> },
      { path: '/features', element: <FeaturesScreen /> },
      { path: '/cart', element: <CartScreen /> },
      { path: '/scan', element: <ScanScreen /> },
      { path: '/orders', element: <OrdersScreen /> },
      { path: '/orders/:id', element: <OrderDetailScreen /> },
      { path: '/ordering/qpo/scan', element: <QpoScanScreen /> },
      { path: '/ordering/qpo/supplier', element: <QpoSupplierScreen /> },
      { path: '/ordering/qpo/supplier/search', element: <QPOSupplierSearchScreen /> },
      { path: '/ordering/qpo/sku-scan', element: <QPOSkuScanScreen /> },
      { path: '/ordering/qpo/sku-search', element: <QPOSkuSearchScreen /> },
      { path: '/ordering/qpo/verify', element: <QPOVerifyScreen /> },
      { path: '/ordering/qpo/summary', element: <QPOSummaryScreen /> },
      { path: '/ordering/qpo/success', element: <QPOSuccessScreen /> },
      { path: '/ordering/qpo/shortfall-claims', element: <QPOShortfallClaimsScreen /> },
      { path: '/ordering/qpo/shortfall-success', element: <QPOShortfallClaimsSuccessScreen /> },
      { path: '/history', element: <HistoryScreen /> },
      { path: '/history/:id', element: <HistoryDetailScreen /> },
      { path: '/history/adj/:id', element: <AdjustmentDetailScreen /> },
      { path: '/history/gi/:id', element: <GiDetailScreen /> },
      { path: '/receiving/success', element: <GrSuccessScreen /> },
      { path: '/receiving/print-slip', element: <GrPrintSlipScreen /> },
      { path: '/receiving/shortfall-claims', element: <ReceivingShortfallClaimsScreen /> },
      { path: '/receiving/shortfall-success', element: <ReceivingShortfallClaimsSuccessScreen /> },
      { path: '/receiving/do/:id', element: <DOReceiveDetailScreen /> },
      { path: '/receiving/do/:id/add-shipment', element: <DOAddShipmentScreen /> },
      { path: '/receiving/do/:id/verify-pallets', element: <DOVerifyPalletsScreen /> },
      { path: '/receiving/do/:id/summary', element: <DOSummaryScreen /> },
      { path: '/receiving/do/:id/scan-truck', element: <DOScanTruckScreen /> },
      { path: '/receiving/:id', element: <ReceiveDetailScreen /> },
      { path: '/receiving/:id/gr-input', element: <GrQtyInputScreen /> },
      { path: '/receiving/:id/gr-scan', element: <GrScanScreen /> },
      { path: '/receiving/:id/summary', element: <GrSummaryScreen /> },
      { path: '/returns', element: <ReturnSelectVendorScreen /> },
      { path: '/returns/add-items', element: <ReturnAddItemsScreen /> },
      { path: '/returns/scan', element: <ReturnScanScreen /> },
      { path: '/returns/search-product', element: <ReturnSearchProductScreen /> },
      { path: '/returns/pending', element: <ReturnPendingScreen /> },
      { path: '/returns/rsto', element: <RstoDetailScreen /> },
      { path: '/issue-goods', element: <IssueGoodsScreen /> },
      { path: '/issue-goods/summary', element: <IssueGoodsSummaryScreen /> },
      { path: '/issue-goods/success', element: <IssueGoodsSuccessScreen /> },
      { path: '/dispose', element: <GiForDisposalScreen /> },
      { path: '/dispose/summary', element: <GiForDisposalSummaryScreen /> },
      { path: '/soh-adjustment', element: <SohAdjustmentScreen /> },
      { path: '/soh-adjustment/create', element: <SohAdjustmentCreateScreen /> },
      { path: '/soh-adjustment/submitted', element: <SohAdjustmentSubmittedScreen /> },
      { path: '/soh-adjustment/scan', element: <SohAdjustmentScanScreen /> },
      { path: '/soh-adjustment/:id', element: <SohAdjustmentDetailScreen /> },
      // Two routes, two screens. Which one a role enters on is decided by the
      // caller (see `stocktakeEntryPath`), not by swapping this element.
      { path: '/stocktake', element: <StocktakeStartScreen /> },
      { path: '/stocktake/setup', element: <StocktakeSetupScreen /> },
      { path: '/stocktake/setup/edit', element: <StocktakeSetupEditScreen /> },
      { path: '/stocktake/setup/sku-search', element: <StocktakeSkuSearchScreen /> },
      { path: '/stocktake/scan-location', element: <StocktakeScanLocScreen /> },
      { path: '/stocktake/scan-sku', element: <StocktakeScanSKUScreen /> },
      { path: '/stocktake/search', element: <StocktakeSearchScreen /> },
      { path: '/stocktake/scan-list', element: <StocktakeScanListScreen /> },
      { path: '/stocktake/success', element: <StocktakeSuccessScreen /> },
      { path: '/accessibility', element: <AccessibilityScreen /> },
      { path: '/tasks/all', element: <AllTasksScreen /> },
      { path: '/tasks/store-tasks/search', element: <StoreTasksSearchScreen /> },
      { path: '/tasks/create', element: <CreateTaskScreen /> },
      { path: '/tasks/:id', element: <TaskDetailScreen /> },
      { path: '/tasks/:id/edit', element: <EditTaskScreen /> },
      { path: '/tasks/:id/start', element: <TaskStartScreen /> },
    ],
  },
]
